import { useEffect, useState } from 'react'

import { MapPin, X, ZoomIn } from 'lucide-react'
import { createPortal } from 'react-dom'

/** Full-screen zoom of a detail photo — a portal overlay (Esc / ✕ / backdrop to close). Rendered into
 *  document.body so it escapes the detail's scroll/stacking contexts and covers the whole screen.
 *  Exported so the Resolution-timeline thumbnails reuse the exact same in-place zoom as the issue photo
 *  (including the capture-phase Esc that closes only the lightbox, never the inline detail). */
export function PhotoLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation() // close the lightbox WITHOUT also collapsing the inline detail
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo"
        className="absolute top-4 right-4 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
      />
    </div>,
    document.body,
  )
}

/** The clickable photo frame: the whole photo (object-contain) over a blurred copy of itself; click →
 *  full-screen zoom, with a hover hint. Its own component so DetailPhoto stays simple. `aspectClass`
 *  sets the frame shape (portrait default; issue drawer passes a landscape one). */
function PhotoFrame({ src, alt, aspectClass }: { src: string; alt: string; aspectClass: string }) {
  const [zoom, setZoom] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setZoom(true)}
        aria-label="Open photo"
        className={`group relative block w-full cursor-zoom-in overflow-hidden rounded-[14px] border border-gis-line bg-gis-track-2 focus-visible:ring-2 focus-visible:ring-blue-500/60 ${aspectClass}`}
      >
        <img src={src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl" />
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-contain" />
        <span className="absolute right-2 bottom-2 grid h-7 w-7 place-items-center rounded-full bg-black/45 text-white opacity-0 transition group-hover:opacity-100">
          <ZoomIn className="h-4 w-4" />
        </span>
      </button>
      {zoom && <PhotoLightbox src={src} alt={alt} onClose={() => setZoom(false)} />}
    </>
  )
}

/**
 * A photo in a detail drawer (issue / site): the WHOLE photo (never cropped, so a geotag watermark
 * stays) in a fixed-aspect frame; the leftover space is filled by a soft blurred copy of the same photo
 * instead of gray bars. Click → full-screen zoom. No `photoUrl` → renders `fallback` (or nothing).
 * `captionAbove` moves the MapPin caption above the image (issue "inline" layout); default is below.
 * `aspectClass` sets the frame shape — pass portrait `aspect-[3/4]` (Site drawer) or landscape
 * `aspect-video` (issue drawer).
 */
export function DetailPhoto({
  photoUrl,
  alt,
  aspectClass,
  caption = null,
  captionAbove = false,
  fallback = null,
  className = '',
}: {
  photoUrl: string | null
  alt: string
  aspectClass: string
  caption?: string | null
  captionAbove?: boolean
  fallback?: React.ReactNode
  className?: string
}) {
  if (!photoUrl) {
    return <>{fallback}</>
  }
  const cap = caption ? (
    <figcaption className={`flex items-center gap-[6px] text-[11.5px] font-medium text-gis-soft ${captionAbove ? 'mb-2' : 'mt-2'}`}>
      <MapPin className="h-[13px] w-[13px] flex-none stroke-2 text-gis-muted" />
      {caption}
    </figcaption>
  ) : null
  return (
    <figure className={className}>
      {captionAbove && cap}
      <PhotoFrame src={photoUrl} alt={alt} aspectClass={aspectClass} />
      {!captionAbove && cap}
    </figure>
  )
}
